import { Box, Divider, styled, Typography, useTheme } from '@mui/material';
import { useAuth } from 'lib/useAuth';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SwipeableListItem } from 'ui/SwipeableListItem';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AddressForm } from './forms/AddressForm';
import { addressApi } from './addressApi';
import { IAddress } from '@pepperhq/types-sdk';
import { AddressView } from './AddressView';
import { isString } from 'lib/typeInference';
import { useUserAddressDelete } from './hooks/useUserAddressDelete';
import { ButtonV2 } from 'ui/ButtonV2';
import { BaseModal } from 'ui/BaseModal';
import { FlatAppbar } from 'ui/FlatAppbar';
import { FlatToolbar } from 'ui/FlatToolbar';
import { NotificationAlert } from 'ui/alert/NotificationAlert';
import { useNotificationAlert } from 'ui/alert/useNotificationAlert';
import logger from 'lib/logger';
import { useDispatch } from 'react-redux';
import { setUserAddresses } from 'store/auth/authActions';
import { REDUCED_CONTENT_MAX_WIDTH } from 'config/constants';
import { LoadingTypography } from 'lib/LoadingTypography';
import { UserAddress } from 'components/user/model/User';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';

const SecondaryTypography = styled(Typography)({
    opacity: 0.7
});

interface AddressBookProps {
    onAddressClick?: (address: UserAddress) => void;
    title: string;
    hideUserAvatar?: boolean;
    disableBack?: boolean;
}

export const AddressBook: React.FC<AddressBookProps> = ({
    onAddressClick,
    title,
    disableBack,
    hideUserAvatar = true
}) => {
    const { t } = useTranslation();
    const { user, isLoading: userLoading } = useAuth();
    const theme = useTheme();
    const dispatch = useDispatch();
    const { logUserEvent } = useGAHelpers();
    const { currentNotification, dismissAlert, showAlert } = useNotificationAlert();
    const [openCreateDialog, setOpenCreateDialog] = React.useState(false);
    const [actionsLoading, setActionsLoading] = React.useState(false);
    const [addresses, setAddresses] = React.useState<UserAddress[]>(user?.addresses ?? []);
    const [selectedAddressId, setSelectedAddressId] = React.useState<string | undefined>();

    const handleOpenCreateDialog = React.useCallback(() => {
        logUserEvent(EGAEventName.AddressBookAddTapped);
        setOpenCreateDialog(true);
    }, [logUserEvent]);
    const handleCloseCreateDialog = React.useCallback(() => {
        setOpenCreateDialog(false);
    }, []);

    const handleCreateAddress = React.useCallback(
        async (body: IAddress) => {
            try {
                setActionsLoading(true);
                const newAddresses = await addressApi.createUserAddress(user?.id ?? '', body);
                setOpenCreateDialog(false);
                dispatch(setUserAddresses(newAddresses));
            } catch (e: any) {
                showAlert('error', t('GENERAL_GENERIC_ERROR'), e.data.message);
            } finally {
                setActionsLoading(false);
            }
        },
        [dispatch, showAlert, t, user?.id]
    );
    const handleViewAddressClose = React.useCallback(() => {
        setSelectedAddressId(undefined);
    }, []);
    const handleSelectAddress = React.useCallback(
        (id: string) => () => {
            if (onAddressClick) {
                const address = addresses.find(item => item._id === id);
                if (address) {
                    onAddressClick(address);
                } else {
                    logger.error('Address not found');
                    throw new Error('Address not found');
                }
            } else {
                setSelectedAddressId(id);
            }
        },
        [addresses, onAddressClick]
    );
    const selectedAddress = React.useMemo(() => {
        if (isString(selectedAddressId)) {
            return addresses.find(item => item._id === selectedAddressId) ?? null;
        }
        return null;
    }, [addresses, selectedAddressId]);

    const handleDeleteSuccess = React.useCallback(
        (data: UserAddress[]) => {
            handleViewAddressClose();
            dispatch(setUserAddresses(data));
        },
        [dispatch, handleViewAddressClose]
    );
    const handleDeleteError = React.useCallback(
        (message?: string) => {
            showAlert('error', t('GENERAL_GENERIC_ERROR'), message ?? t('GENERAL_GENERIC_ERROR'));
        },
        [showAlert, t]
    );
    const { deleteLoading, onDelete, onSwipeDelete } = useUserAddressDelete(
        handleDeleteSuccess,
        handleDeleteError
    );

    React.useEffect(() => {
        setAddresses(user?.addresses ?? []);
    }, [user?.addresses]);

    // TODO: potentially move it to a separate component? Revisit
    const renderAddressItem = React.useCallback(
        (entry: UserAddress) => (
            <Box width="100%" key={entry._id} onClick={handleSelectAddress(entry._id)}>
                <SwipeableListItem
                    enableRightToLeft={!onAddressClick}
                    onSwipeLeft={onSwipeDelete(entry._id)}
                    rightToLeftBg={theme.palette.error.main}
                    rightToLeftIcon={<DeleteIcon sx={{ fontSize: '1.5rem', color: 'error.contrastText' }} />}
                >
                    <Box
                        p={1}
                        pl={2}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        flex={1}
                    >
                        <Box>
                            {entry.name && <Typography sx={{ fontWeight: 700 }}>{entry.name}</Typography>}
                            <Typography sx={entry.name ? { opacity: 0.7 } : { fontWeight: 700 }}>
                                {entry.address?.line1}
                            </Typography>
                            {entry.address?.line2 && (
                                <SecondaryTypography>{entry.address.line2}</SecondaryTypography>
                            )}
                            <SecondaryTypography>
                                {entry.address?.city}
                                {entry.address?.region ? `, ${entry.address.region}` : ''}
                            </SecondaryTypography>
                            <SecondaryTypography>{entry.address?.postalCode}</SecondaryTypography>
                        </Box>
                        <ChevronRightIcon />
                    </Box>
                </SwipeableListItem>
                <Divider variant="middle" />
            </Box>
        ),
        [handleSelectAddress, onAddressClick, onSwipeDelete, theme.palette.error.main]
    );
    const renderAddressItemSkeleton = React.useCallback(
        (key: string) => (
            <Box width="100%" key={key}>
                <SwipeableListItem
                    rightToLeftBg={theme.palette.error.main}
                    rightToLeftIcon={<DeleteIcon sx={{ fontSize: '1.5rem', color: 'error.contrastText' }} />}
                >
                    <Box
                        p={1}
                        pl={2}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        flex={1}
                    >
                        <Box>
                            <LoadingTypography sx={{ fontSize: '1.4rem', pb: 0.5 }} loading width={150} />
                            <LoadingTypography sx={{ fontSize: '1.3rem' }} loading width={300} />
                        </Box>
                        <ChevronRightIcon />
                    </Box>
                </SwipeableListItem>
                <Divider variant="middle" />
            </Box>
        ),
        [theme.palette.error.main]
    );
    return (
        <Box
            width="100%"
            maxWidth={REDUCED_CONTENT_MAX_WIDTH}
            marginX="auto"
            minHeight="100%"
            sx={{ backgroundColor: 'background.paper' }}
            className="hidden-scroll"
        >
            <FlatAppbar variant="dense">
                <FlatToolbar
                    title={title}
                    hideUserAvatar={hideUserAvatar}
                    disableEdit
                    disableBack={disableBack}
                />
            </FlatAppbar>
            {!userLoading && addresses.length === 0 && (
                <Box
                    width="100%"
                    height="100%"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="flex-start"
                >
                    <Typography color="primary" align="center" sx={{ fontSize: '1.2rem', p: 1 }}>
                        {t('DELIVERY_NO_ADDRESSES_MESSAGE')}
                    </Typography>
                </Box>
            )}
            {!userLoading && addresses.length > 0 && (
                <Box position="relative">{addresses.map(renderAddressItem)}</Box>
            )}
            {userLoading && <Box>{renderAddressItemSkeleton('skeleton-item-0')}</Box>}
            <Box width="100%" height="64px" />
            <Box
                flex={1}
                display="flex"
                alignContent="flex-end"
                flexDirection="column"
                position="absolute"
                bottom={0}
                left="50%"
                width="100%"
                maxWidth={REDUCED_CONTENT_MAX_WIDTH}
                p={1}
                sx={{ transform: 'translateX(-50%)' }}
            >
                <ButtonV2
                    variant="contained"
                    fullWidth
                    onClick={handleOpenCreateDialog}
                    disabled={userLoading || actionsLoading || deleteLoading}
                >
                    {t('DELIVERY_ADDRESS_ADD_TITLE')}
                </ButtonV2>
            </Box>
            <BaseModal fullScreen open={openCreateDialog} paperSx={{ p: 0 }}>
                <AddressForm onClose={handleCloseCreateDialog} onCreate={handleCreateAddress} />
            </BaseModal>
            <BaseModal fullScreen open={!!selectedAddress} paperSx={{ p: 0, width: '100%' }}>
                <AddressView
                    onClose={handleViewAddressClose}
                    address={selectedAddress}
                    onDelete={onDelete}
                    loading={actionsLoading || deleteLoading}
                />
            </BaseModal>
            <NotificationAlert notification={currentNotification} onCancel={dismissAlert} />
        </Box>
    );
};
